import { useEffect, useState } from 'react';
import Modal from '../modal/Modal';
import SortedTable from '../sorted-table/SortedTable';
import FilterDropdown, { FilterOption } from '../custom-input/FilterDropdown';
import { BulkTransaction, InputShippingAddress } from '../../types/interfaces';
import { TransactionStatusEnum } from '../../types/enums';
import { extractNotesInfo } from '../../utils/transactionUtils';
import { getInventoryItemId } from '../../services/inventoryTransaction.service';
import {
  ADDRESS_CONSTANTS,
  ANIMAL_CONSTANTS,
  BUTTON_CONSTANTS,
  INVENTORY_MANAGEMENT_CONSTANTS,
  LABEL_CONSTANTS,
  PAGE_HEADER_CONSTANTS,
  TABLE_HEADER_CONSTANTS,
} from '../../constants/common';
import { APP_PATHS } from '../../constants/routePaths';
import './mergeTransactionModal.scss';

export type MergeTransactionModalProps = {
  isOpen: boolean;
  width?: number;
  setIsOpen: (e: boolean) => void;
  pendingTransactions: BulkTransaction[];
  onCreateNewOrder: () => void;
  onUpdateExistingOrder: (e: number) => void;
};

const MergeTransactionModal = ({
  isOpen,
  width = 800,
  setIsOpen,
  pendingTransactions,
  onCreateNewOrder,
  onUpdateExistingOrder,
}: MergeTransactionModalProps) => {
  const [pendingTransactionOptions, setPendingTransactionOptions] = useState<FilterOption<any>[]>([]);
  const [shippingInfo, setShippingInfo] = useState<InputShippingAddress>();
  const [selectedPendingTransaction, setSelectedPendingTransaction] = useState<BulkTransaction>();

  useEffect(() => {
    if (pendingTransactions && pendingTransactions.length > 0) {
      const options = pendingTransactions.map(transaction => ({
        name: `${transaction.bulkOrderId} - ${new Date(transaction.breedDate.toString() as string).toLocaleDateString()}`,
        value: transaction.bulkOrderId,
        optionWarning: transaction.status === `${TransactionStatusEnum.Processed}`,
        message: transaction.status === `${TransactionStatusEnum.Processed}` ? `${TransactionStatusEnum.Processed}` : undefined,
      }));
      setPendingTransactionOptions(options);
      setSelectedPendingTransaction(pendingTransactions[0]);
      handleShippingInfo(pendingTransactions[0]);
    }
  }, [pendingTransactions]);

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleDropdownChange = (selectedOption: FilterOption<any>) => {
    const matchingTransaction = pendingTransactions.find((transaction: any) => transaction.bulkOrderId === selectedOption.value);
    setSelectedPendingTransaction(matchingTransaction || undefined);
    handleShippingInfo(matchingTransaction);
  };

  const handleShippingInfo = (selectedTrans?: BulkTransaction) => {
    const notesInfo = extractNotesInfo(selectedTrans?.notes ?? '');
    setShippingInfo(notesInfo.shippingAddress);
  };

  const isProcessedAndNotInTransactionPage = () =>
    selectedPendingTransaction?.status === TransactionStatusEnum.Processed &&
    !window.location.href.includes(APP_PATHS.TRANSACTION_MANAGEMENT_);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} width={width} ignoreBackdrop>
      <div className="merge-transaction-modal">
        <div className="header">
          <h4>{PAGE_HEADER_CONSTANTS.EXISTING_ORDERS_FOUND}</h4>
        </div>
        <div className="body">
          {pendingTransactions.some(transaction => transaction.status === 'Pending') && (
            <p className="header-notes">{INVENTORY_MANAGEMENT_CONSTANTS.EXISTING_ORDER_HEADER_NOTE}</p>
          )}
          <FilterDropdown
            options={pendingTransactionOptions}
            value={
              selectedPendingTransaction
                ? {
                    name: `${selectedPendingTransaction.bulkOrderId} - ${new Date(
                      selectedPendingTransaction.breedDate,
                    ).toLocaleDateString()} ${
                      selectedPendingTransaction.status === TransactionStatusEnum.Processed
                        ? ` (${selectedPendingTransaction.status})`
                        : ''
                    }`,
                    value: selectedPendingTransaction.bulkOrderId,
                  }
                : { name: '', value: undefined }
            }
            onChange={e => handleDropdownChange(e)}
          />
          {selectedPendingTransaction &&
            (isProcessedAndNotInTransactionPage() ? (
              <div className="header-notes">
                <p>
                  This transaction has been marked as <strong>Processed</strong> and can no longer be edited. For further
                  assistance or updates, kindly contact RSG.
                </p>
              </div>
            ) : (
              <div className="selected-info">
                <h4>{ANIMAL_CONSTANTS.ANIMAL_INFO}</h4>
                <div className="specimens-table">
                  <SortedTable
                    headers={[
                      { displayName: TABLE_HEADER_CONSTANTS.ANIMAL_NAME },
                      { displayName: TABLE_HEADER_CONSTANTS.INVENTORY_LOT_NUMBER },
                      { displayName: TABLE_HEADER_CONSTANTS.CHANGED_QUANTITY },
                    ]}
                    data={selectedPendingTransaction?.inventoryTransactions?.map((trans: any) => {
                      return [
                        {
                          content: trans?.specimen?.animal?.code
                            ? trans?.specimen?.animal?.code + ' - ' + trans?.specimen?.animal?.name
                            : '-',
                        },
                        { content: getInventoryItemId(trans?.specimen) },
                        { content: trans?.changeQuantity?.toString() },
                      ];
                    })}
                    height={214}
                  />
                </div>
                {shippingInfo && (
                  <>
                    <h4>{LABEL_CONSTANTS.SHIPPING_INFO}</h4>
                    <div className="row">
                      <div className="l-column">{ADDRESS_CONSTANTS.ADDRESS_LINE_1}: </div>
                      <div className="r-column">{shippingInfo?.address1}</div>
                    </div>
                    <div className="row">
                      <div className="l-column">{ADDRESS_CONSTANTS.ADDRESS_LINE_2}: </div>
                      <div className="r-column">{shippingInfo?.address2}</div>
                    </div>
                    <div className="row">
                      <div className="l-column">{ADDRESS_CONSTANTS.CITY}: </div>
                      <div className="r-column">{shippingInfo?.city}</div>
                    </div>
                    <div className="row">
                      <div className="l-column">{ADDRESS_CONSTANTS.STATE}: </div>
                      <div className="r-column">{shippingInfo?.state}</div>
                    </div>
                    <div className="row">
                      <div className="l-column">{ADDRESS_CONSTANTS.ZIP_CODE}: </div>
                      <div className="r-column">{shippingInfo?.zipCode}</div>
                    </div>
                    <div className="row">
                      <div className="l-column">{ADDRESS_CONSTANTS.COUNTRY_CODE}: </div>
                      <div className="r-column">{shippingInfo?.country}</div>
                    </div>
                    <p className="footer-notes">{INVENTORY_MANAGEMENT_CONSTANTS.EXISTING_ORDER_FOOTER_NOTE}</p>
                    <p className="footer-notes">
                      If you wish to ship to a different location, please click on <em>{BUTTON_CONSTANTS.CREATE_NEW_ORDER}</em>.
                    </p>
                  </>
                )}
              </div>
            ))}
        </div>
        <div className="footer">
          <button className="button red inverted small" onClick={handleClose}>
            {BUTTON_CONSTANTS.CANCEL}
          </button>
          <button className="button blue inverted medium" onClick={onCreateNewOrder}>
            {BUTTON_CONSTANTS.CREATE_NEW_ORDER}
          </button>
          {!isProcessedAndNotInTransactionPage() && (
            <button className="button green" onClick={() => onUpdateExistingOrder(selectedPendingTransaction?.bulkOrderId!)}>
              {BUTTON_CONSTANTS.UPDATE_EXISTING_ORDER}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default MergeTransactionModal;
